/** EventSource-shaped streaming fetch for native sessions that need auth headers. */
export interface ApiEventSource extends EventTarget { readonly readyState: number; close(): void }
export interface SseFrame { event: string; data: string; id: string }

/** Incremental UTF-8 SSE decoder: CR/LF, split chunks, comments and multiline data. */
export class SseDecoder {
  private decoder = new TextDecoder()
  private buffer = ''
  private data: string[] = []
  private event = ''
  private size = 0
  id = ''
  retry = 3000
  constructor(private emit: (frame: SseFrame) => void) {}
  push(bytes: Uint8Array) {
    this.buffer += this.decoder.decode(bytes, { stream: true })
    if (this.buffer.length + this.size > 1024 * 1024) throw new Error('SSE frame exceeds 1 MiB')
    for (;;) {
      const end = this.buffer.search(/[\r\n]/)
      if (end < 0 || (this.buffer[end] === '\r' && end === this.buffer.length - 1)) return
      const line = this.buffer.slice(0, end)
      const length = this.buffer[end] === '\r' && this.buffer[end + 1] === '\n' ? 2 : 1
      this.buffer = this.buffer.slice(end + length)
      if (!line) {
        if (this.data.length) this.emit({ event: this.event || 'message', data: this.data.join('\n'), id: this.id })
        this.data = []; this.event = ''; this.size = 0
        continue
      }
      if (line.startsWith(':')) continue
      const colon = line.indexOf(':')
      const field = colon < 0 ? line : line.slice(0, colon)
      let value = colon < 0 ? '' : line.slice(colon + 1)
      if (value.startsWith(' ')) value = value.slice(1)
      if (field === 'data') { this.data.push(value); this.size += value.length }
      else if (field === 'event') this.event = value
      else if (field === 'id' && !value.includes('\0')) this.id = value
      else if (field === 'retry' && /^\d+$/.test(value)) this.retry = Math.max(100, Math.min(30000, Number(value)))
    }
  }
}

export class FetchEventSource extends EventTarget implements ApiEventSource {
  readyState = 0
  private controller?: AbortController
  private timer?: ReturnType<typeof setTimeout>
  private lastEventId = ''
  private retry = 3000
  constructor(private path: string, private fetcher: (path: string, init: RequestInit) => Promise<Response>) {
    super()
    // Give consumers time to register listeners, including when a test adapter resolves immediately.
    queueMicrotask(() => { void this.connect() })
  }
  close() {
    this.readyState = 2
    clearTimeout(this.timer)
    this.controller?.abort()
  }
  private async connect() {
    if (this.readyState === 2) return
    this.controller = new AbortController()
    const signal = this.controller.signal
    try {
      const headers = new Headers({ accept: 'text/event-stream' })
      if (this.lastEventId) headers.set('last-event-id', this.lastEventId)
      const response = await this.fetcher(this.path, { headers, signal, cache: 'no-store' })
      if ([204, 401, 403].includes(response.status)) {
        await response.body?.cancel()
        this.close()
        this.dispatchEvent(new Event('error'))
        return
      }
      if (!response.ok || !response.headers.get('content-type')?.startsWith('text/event-stream') || !response.body) {
        await response.body?.cancel()
        throw new Error('Invalid SSE response')
      }
      if (signal.aborted) { await response.body.cancel(); return }
      this.readyState = 1
      this.dispatchEvent(new Event('open'))
      const parser = new SseDecoder(frame => {
        if (signal.aborted) return
        this.lastEventId = frame.id
        this.dispatchEvent(new MessageEvent(frame.event, { data: frame.data, lastEventId: frame.id }))
      })
      parser.id = this.lastEventId
      parser.retry = this.retry
      const reader = response.body.getReader()
      try {
        while (!signal.aborted) {
          const next = await reader.read()
          if (next.done) break
          parser.push(next.value)
          this.lastEventId = parser.id
          this.retry = parser.retry
        }
      } finally { await reader.cancel().catch(() => {}); reader.releaseLock() }
    } catch {
      // Reconnection obtains fresh credentials through the configured fetcher.
    }
    if (signal.aborted) return
    this.readyState = 0
    this.dispatchEvent(new Event('error'))
    if (signal.aborted) return
    this.timer = setTimeout(() => { void this.connect() }, this.retry)
  }
}
